/**
 * The microphone, as the backend wants it (docs/NEW-UI/protocollo.md §6): PCM int16 little-endian,
 * 16 kHz, mono, 320-sample (20 ms) frames with no header. The 20 ms are load-bearing: the server
 * counts every frame as 20 ms of speech.
 *
 * Who cancels the echo is the `aec` flag: on the totem the eMeet speakerphone does it (`os`), so the
 * browser's own canceller must stay off — two cancellers on one signal fight.
 */
import type { AecMode } from '../core/flags';

export const MIC_RATE = 16000;
export const FRAME_SAMPLES = 320;

// Nearest-neighbour resampler to 16 kHz in an AudioWorklet (the old kiosk's, unchanged:
// backend/frontend/src/hooks/voice/micCapture.ts). Loaded from a data: URI — Safari fails on blob: URLs.
const WORKLET = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor() { super(); this.buffer = new Float32Array(0); this.ratio = sampleRate / ${MIC_RATE}; }
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const samples = input[0];
    const ratio = this.ratio;
    if (ratio <= 1.01) {
      const next = new Float32Array(this.buffer.length + samples.length);
      next.set(this.buffer); next.set(samples, this.buffer.length); this.buffer = next;
    } else {
      const outLen = Math.floor(samples.length / ratio);
      const next = new Float32Array(this.buffer.length + outLen);
      next.set(this.buffer);
      for (let i = 0; i < outLen; i++) next[this.buffer.length + i] = samples[Math.round(i * ratio)];
      this.buffer = next;
    }
    while (this.buffer.length >= ${FRAME_SAMPLES}) {
      this.port.postMessage({ chunk: this.buffer.slice(0, ${FRAME_SAMPLES}) });
      this.buffer = this.buffer.slice(${FRAME_SAMPLES});
    }
    return true;
  }
}
registerProcessor('pcm-processor', PcmProcessor);
`;
const WORKLET_URI = `data:application/javascript;charset=utf-8,${encodeURIComponent(WORKLET)}`;

/** What the browser actually gave us, sent once to the backend (`mic_settings`) for its telemetry. */
export interface MicSettings {
  echoCancellation?: boolean | string;
  noiseSuppression?: boolean | string;
  autoGainControl?: boolean;
  sampleRate?: number;
  deviceId?: string;
  label?: string;
  aecMode: AecMode;
  /** If not 16000 the resampler above is running. */
  contextSampleRate: number;
}

export interface Microphone {
  settings: MicSettings;
  stop(): void;
}

export function float32ToPcm16(samples: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

/** Why the microphone cannot open on this page at all, before asking: a browser refuses it outside HTTPS. */
export function microphoneBlocker(win: Pick<Window, 'isSecureContext' | 'location'> & { navigator: Navigator } = window): string | null {
  if (!win.isSecureContext) {
    return `the microphone needs HTTPS: open https://${win.location.host}${win.location.pathname} (backend/kiosk-ui/README.md, «Da un telefono o un tablet»)`;
  }
  if (!win.navigator.mediaDevices?.getUserMedia) return 'this browser gives no microphone to web pages';
  return null;
}

/**
 * The context the worklet runs in, fed by the stream. At 16 kHz first: the browser resamples, as the totem's Chrome
 * always has. A browser that will not connect a stream to a context of another rate (Firefox; some Safari) gets one at
 * the device's own rate, and the worklet's resampler (ratio > 1) does the work.
 */
async function connectedContext(stream: MediaStream): Promise<{ context: AudioContext; source: MediaStreamAudioSourceNode }> {
  try {
    const context = new AudioContext({ sampleRate: MIC_RATE });
    try {
      return { context, source: context.createMediaStreamSource(stream) };
    } catch (e) {
      void context.close();
      throw e;
    }
  } catch {
    const context = new AudioContext();
    return { context, source: context.createMediaStreamSource(stream) };
  }
}

/** Open the microphone and call `onFrame` with every 20 ms PCM frame. Throws if refused. */
export async function startMicrophone(aec: AecMode, onFrame: (pcm: ArrayBuffer) => void): Promise<Microphone> {
  const blocker = microphoneBlocker();
  if (blocker) throw new Error(blocker);
  const browserAec = aec === 'browser';
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { sampleRate: MIC_RATE, channelCount: 1, echoCancellation: browserAec, noiseSuppression: browserAec, autoGainControl: browserAec },
  });
  const track = stream.getAudioTracks()[0];
  const negotiated = track?.getSettings() ?? {};
  const { context, source } = await connectedContext(stream);
  if (context.state === 'suspended') await context.resume();
  await context.audioWorklet.addModule(WORKLET_URI);
  const worklet = new AudioWorkletNode(context, 'pcm-processor');
  worklet.port.onmessage = (e: MessageEvent<{ chunk: Float32Array }>) => onFrame(float32ToPcm16(e.data.chunk));
  source.connect(worklet);
  worklet.connect(context.destination); // keeps the worklet processing; it outputs nothing

  return {
    settings: {
      echoCancellation: negotiated.echoCancellation,
      noiseSuppression: negotiated.noiseSuppression,
      autoGainControl: negotiated.autoGainControl,
      sampleRate: negotiated.sampleRate,
      deviceId: negotiated.deviceId,
      label: track?.label,
      aecMode: aec,
      contextSampleRate: context.sampleRate,
    },
    stop() {
      worklet.port.onmessage = null;
      source.disconnect();
      worklet.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      void context.close();
    },
  };
}
